/**
 * The server-grant token source (the MyNoats pattern from FINN Spaces): the proxy holds a refresh
 * token per recruiter and mints one-hour access tokens from it on request — no window, no click.
 * One of the three token sources behind useGmailToken.ts; the ordering and reasoning live there.
 */
import { mintGmailToken, startGmailConnect } from '../../services/taGmailOauthApi';
import { EXPIRY_SLACK_MS, GMAIL_SCOPES, grantCoversScopes, type Grant } from './gmailGrant';

export { startGmailConnect };

/**
 * Page-lifetime memory that the proxy answered "not configured here". One 503 is enough: the
 * answer cannot change without a deploy, and asking again on every mount would only add a failed
 * request in front of every popup.
 */
let unavailable = false;

export const isServerGrantUnavailable = () => unavailable;
export function markServerGrantUnavailable() {
  unavailable = true;
}
/** @internal test seam. */
export function resetServerGrantMemory() {
  unavailable = false;
}

export type ServerMint =
  | { grant: Grant }
  | { needsReconnect: true }
  /** Transient: the proxy or Google could not be reached. Nothing is known about the grant. */
  | { error: string }
  /** No grant here, or the deployment has no server flow at all. */
  | { none: true };

/**
 * A fresh token from the proxy's stored grant. Never throws, never opens anything.
 *
 * `error` is kept apart from `none` on purpose: a recruiter who HAS a grant must not be sent through
 * Google's consent page again because the proxy hiccupped once — connect() shows the failure instead.
 */
export async function mintFromServer(userEmail: string): Promise<ServerMint> {
  if (unavailable) return { none: true };
  const owner = userEmail.trim().toLowerCase();
  const result = await mintGmailToken();
  switch (result.kind) {
    case 'token': {
      const grant: Grant = {
        token: result.token.accessToken,
        owner,
        // From the lifetime, on THIS clock — see ServerGrantToken.expiresIn.
        expiresAt: Date.now() + result.token.expiresIn * 1000 - EXPIRY_SLACK_MS,
        // What Google GRANTED (the proxy reads it off the refresh response). An empty list means the
        // proxy could not tell; it has already checked coverage server-side, so record the request.
        scopes: result.token.scopes.length ? result.token.scopes : [...GMAIL_SCOPES],
        source: 'server',
      };
      // The proxy refuses narrow grants itself; this is the belt to that suspenders.
      return grantCoversScopes(grant) ? { grant } : { needsReconnect: true };
    }
    case 'unavailable':
      unavailable = true;
      return { none: true };
    case 'needs_reconnect':
      return { needsReconnect: true };
    case 'error':
      return { error: result.message };
    default:
      return { none: true };
  }
}

/** Where Google should send the browser back to: this very page, board selection included. */
export function currentReturnPath(): string {
  return `${window.location.pathname}${window.location.search}`;
}

let navigate: (url: string) => void = (url) => window.location.assign(url);
/** Leave for Google's consent page. */
export function navigateToConsent(url: string) {
  navigate(url);
}
/** @internal test seam — capture the consent-page navigation instead of leaving the page. */
export function setNavigateForTests(fn: ((url: string) => void) | null) {
  navigate = fn || ((url) => window.location.assign(url));
}

/** Query flags the proxy's OAuth callback puts on the return URL. */
const RETURN_FLAG = 'gmail';
const RETURN_ERROR = 'gmail_error';

/** Recruiter-facing copy for the short codes the proxy's callback puts on the URL. */
function describeReturnError(code: string): string {
  switch (code) {
    case 'access_denied':
      return 'Google access was declined before it was granted.';
    case 'wrong_account':
      return 'That is a different Google account than the one you are signed in with.';
    case 'missing_scope':
      return 'Not all permissions were approved — Gmail needs every one of them to work here.';
    case 'expired':
      return 'The connection attempt took too long. Try again.';
    default:
      return 'Could not connect Gmail. Try again.';
  }
}

export interface ReturnFlags {
  connected: boolean;
  error: string | null;
}

/**
 * The flags most recently read, kept for a moment. React StrictMode runs state initialisers twice
 * in the same render; the second run must see the same answer, not a URL the first run already
 * cleaned. Kept only briefly so a later remount (navigating away and back) does not replay a
 * message that was already shown.
 */
let consumed: { flags: ReturnFlags; at: number } | null = null;
const REPLAY_WINDOW_MS = 2_000;
/** @internal test seam. */
export function resetReturnFlagsForTests() {
  consumed = null;
}

/**
 * Read and remove the callback's flags from the URL. Removed so a reload or a copied link does not
 * replay the message; `replaceState` so the back button is not burdened with it either.
 */
export function consumeReturnFlags(): ReturnFlags {
  if (consumed && Date.now() - consumed.at < REPLAY_WINDOW_MS) return consumed.flags;
  consumed = { flags: readAndStripReturnFlags(), at: Date.now() };
  return consumed.flags;
}

function readAndStripReturnFlags(): ReturnFlags {
  try {
    const url = new URL(window.location.href);
    const connected = url.searchParams.get(RETURN_FLAG) === 'connected';
    const code = url.searchParams.get(RETURN_ERROR);
    if (!connected && !code) return { connected: false, error: null };
    url.searchParams.delete(RETURN_FLAG);
    url.searchParams.delete(RETURN_ERROR);
    window.history.replaceState(window.history.state, '', url.toString());
    return { connected, error: code ? describeReturnError(code) : null };
  } catch {
    return { connected: false, error: null };
  }
}
